import { request } from "./client";
import type {
  AlternativeRoute,
  OsrmRouteSummary,
  RoutePlan,
  RoutingQueryInput,
  RoutingVenueSearchResult,
} from "@/types/routing";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const normalizeDate = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return trimmed;
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  const fallback = trimmed.slice(0, 10);
  return DATE_ONLY_PATTERN.test(fallback) ? fallback : null;
};

const normalizeQuery = (query: RoutingQueryInput) => ({
  states: query.states && query.states.length ? query.states : ["CO"],
  start_city: query.start_city || "Denver",
  start_state: query.start_state || "CO",
  end_city: query.end_city || null,
  end_state: query.end_state || null,
  priority_cities: query.priority_cities ?? [],
  start_date: normalizeDate(query.start_date),
  end_date: normalizeDate(query.end_date),
  max_drive_hours: query.max_drive_hours ?? null,
  target_show_frequency_days: query.target_show_frequency_days ?? null,
  pinned_cities: query.pinned_cities ?? [],
  use_real_routing: query.use_real_routing ?? false,
});

export async function suggestRoute(query: RoutingQueryInput): Promise<RoutePlan> {
  const payload = normalizeQuery(query);
  return request("/routing/suggest", {
    method: "POST",
    body: payload,
  });
}

export async function suggestAlternatives(query: RoutingQueryInput): Promise<AlternativeRoute[]> {
  const payload = normalizeQuery(query);
  const response = await request("/routing/alternatives", {
    method: "POST",
    body: payload,
  });
  return (response?.alternatives ?? []) as AlternativeRoute[];
}

export async function updateRoutePlan(plan: RoutePlan, confirmedCity: string): Promise<RoutePlan> {
  return request("/routing/update", {
    method: "POST",
    body: {
      plan,
      confirmed_city: confirmedCity,
    },
  });
}

export async function adviseRoute(plan: RoutePlan): Promise<{ advice: string }> {
  return request("/routing/advise", {
    method: "POST",
    body: plan,
  });
}

export async function getOsrmRoute(points: { lat: number; lon: number }[]): Promise<OsrmRouteSummary> {
  return request("/routing/osrm-route", {
    method: "POST",
    body: { points },
  });
}

type RoutingVenueSearchParams = {
  city?: string;
  state?: string;
  states?: string[];
  radiusMiles?: number;
  limit?: number;
};

export async function searchRoutingVenues(params: RoutingVenueSearchParams = {}): Promise<RoutingVenueSearchResult[]> {
  const query = new URLSearchParams();
  if (params.city) query.set("city", params.city);
  if (params.state) query.set("state", params.state);
  if (params.states?.length) query.set("states", params.states.join(","));
  if (typeof params.radiusMiles === "number") query.set("radius_miles", String(params.radiusMiles));
  if (typeof params.limit === "number") query.set("limit", String(params.limit));
  const suffix = query.toString();
  const response = await request(`/routing/venues/search${suffix ? `?${suffix}` : ""}`);
  if (!Array.isArray(response)) {
    return [];
  }
  return response as RoutingVenueSearchResult[];
}
